import React from 'react';
import { Button } from './button';
import { cn } from '@lib/utils';

type ShareButtonVariant = 'default' | 'icon-only' | 'compact-pill';

interface ShareButtonProps {
  onClick?: (e: React.MouseEvent) => void;
  variant?: ShareButtonVariant;
  disabled?: boolean;
  className?: string;
  /** Background class. Used by `compact-pill` for scroll-linked dissolve in compact headers. */
  bgClassName?: string;
  /** Accessible label. Defaults to "Share". */
  ariaLabel?: string;
  /**
   * `compact-pill` only: smoothly collapse the "Share" label to nothing so the button reads as
   * icon-only (icon + aria-label kept). Wired to the sticky header's title reveal so Share yields
   * its width to the pinned event title. Ignored by other variants. Default keeps the label.
   */
  collapsed?: boolean;
}

/**
 * A 44px hit area around the default 32px Share (6px above and below), the look unchanged: for poll cards and the
 * live sheet (Loop A PC8, L27). Its own export so other Share uses keep the default untouched.
 */
export const SHARE_HIT_AREA_44 =
  "relative before:absolute before:-inset-y-1.5 before:inset-x-0 before:content-['']";

/** Custom Share icon matching the app's design */
export const ShareIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.2552 9.74102L11.3394 3.99597C11.1602 3.82176 10.8687 3.95461 10.8687 4.21049V7.08353C10.8687 7.23344 10.7608 7.35905 10.6183 7.37456C9.57995 7.4857 7.84971 7.83927 6.56687 8.95582C4.44861 10.7981 3.83342 14.2284 3.65822 15.8365C3.62803 16.1152 3.9522 16.2754 4.13829 16.0733C4.85197 15.2979 6.20657 13.9999 7.86703 13.2468C9.10978 12.6828 9.98727 12.4952 10.5698 12.4554C10.7316 12.4445 10.8682 12.5784 10.8682 12.7474V15.7006C10.8682 15.956 11.1597 16.0888 11.3389 15.9151L17.2547 10.1701C17.374 10.0543 17.374 9.85733 17.2547 9.74154L17.2552 9.74102Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * Reusable Share Button component with consistent styling across the app.
 *
 * Variants:
 * - default: Full button with icon and "Share" label (w-auto h-8 px-2)
 * - icon-only: Icon only button for header/compact spaces (w-10 h-10)
 * - compact-pill: Labelled pill for scroll-linked compact headers. Renders a
 *   plain <button> (not shadcn Button) so the 20px ShareIcon isn't shrunk by
 *   the Button base's `[&_svg]:size-4` rule. Pass `bgClassName` for the
 *   scroll-linked background dissolve.
 */
export const ShareButton: React.FC<ShareButtonProps> = ({
  onClick,
  variant = 'default',
  disabled = false,
  className = '',
  bgClassName = '',
  ariaLabel,
  collapsed = false,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(e);
  };

  if (variant === 'compact-pill') {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        aria-label={ariaLabel ?? 'Share'}
        className={cn(
          'h-9 px-3 flex items-center rounded-full transition-colors duration-300 font-fira font-normal text-sm text-gray-800 disabled:opacity-50 disabled:pointer-events-none',
          // Drop the icon↔label gap when collapsed so the lone icon stays optically centered.
          collapsed ? '' : 'gap-[4px]',
          bgClassName,
          className,
        )}
      >
        <ShareIcon className="w-5 h-5" />
        {/* Collapses to zero width (matching the 300ms logo↔title cross-fade) so Share reads as
            icon-only once the sticky title pins; the aria-label keeps it accessible throughout. */}
        <span
          className={cn(
            'overflow-hidden transition-all duration-300',
            collapsed ? 'max-w-0 opacity-0' : 'max-w-[60px] opacity-100',
          )}
        >
          Share
        </span>
      </button>
    );
  }

  const variantStyles: Record<Exclude<ShareButtonVariant, 'compact-pill'>, string> = {
    default:
      'w-auto h-8 px-2 gap-1 rounded-full text-gray-600 shadow-none bg-transparent border-none',
    'icon-only': 'w-10 h-10 rounded-full text-gray-600 hover:bg-gray-100/80 shadow-none',
  };

  const buttonVariant = variant === 'icon-only' ? 'ghost' : 'outline-solid';
  const showLabel = variant !== 'icon-only';

  return (
    <Button
      variant={buttonVariant}
      size="icon"
      onClick={handleClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={`${variantStyles[variant]} ${className}`}
    >
      <ShareIcon className="w-5 h-5" />
      {showLabel && <span>Share</span>}
    </Button>
  );
};
